import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { useAuth } from '../context/AuthContext';
import { getSocket } from '../lib/socket';
import { playTicketChime } from '../utils/soundAlert';

/** Purpose: normalize API/socket identity shapes to a comparable string. Why: duplicate and recipient checks must work across populated and raw IDs. */
const normalizeId = (value) => String(value?._id || value?.id || value || '');

/** Purpose: test a conversation tag without casing/spacing differences. Why: incoming events can contain tags from multiple legacy sources. */
const hasTag = (conversation, tag) =>
  Array.isArray(conversation?.tags) &&
  conversation.tags.some((t) => String(t || '').trim().toLowerCase() === String(tag).trim().toLowerCase());

/** Purpose: read the normalized handoff reason from event metadata. Why: ticket classification relies on the same normalized value everywhere. */
const getHandoffReason = (conversation) =>
  String(conversation?.metadata?.handoffReason || '').trim().toLowerCase();

/** Purpose: recognize ticket-like conversations for global alerts. Why: notification events can arrive in different API shapes and should use one classifier. */
export const isTicketConversation = (conversation) => {
  if (!conversation) return false;
  const handoffReason = getHandoffReason(conversation);
  const status = String(conversation?.status || '').trim().toLowerCase();
  const hasLiveAgentTag = hasTag(conversation, 'ticket')
    || hasTag(conversation, 'live_chat')
    || hasTag(conversation, 'live_agent')
    || hasTag(conversation, 'support');
  const hasTicketMessage = typeof conversation.lastMessage === 'string' &&
    /assigned to/i.test(conversation.lastMessage);

  return Boolean(
    hasLiveAgentTag ||
    ['raise_ticket', 'chat_handoff', 'live_agent', 'live_chat'].includes(handoffReason) ||
    conversation.ticketId ||
    conversation.metadata?.ticketId ||
    conversation.assignedTo ||
    conversation.ownerType === 'agent' ||
    ['assigned', 'queued', 'pending'].includes(status) ||
    conversation.metadata?.handoffRequested ||
    conversation.metadata?.handoffQueueActive ||
    conversation.metadata?.handoffAssignedAt ||
    conversation.metadata?.handoffAssignedMessage ||
    conversation.metadata?.systemEvent === 'conversation.assigned' ||
    hasTicketMessage
  );
};

/** Purpose: identify human-support conversations eligible for ticket alerts. Why: support assignment notifications should not fire for bot-only messages. */
export const isSupportConversation = (conversation) => {
  if (!conversation) return false;

  // Never notify on resolved or closed conversations
  if (conversation.status === 'resolved' || conversation.status === 'closed') {
    return false;
  }

  const hasLiveAgentTag = hasTag(conversation, 'ticket')
    || hasTag(conversation, 'live_chat')
    || hasTag(conversation, 'live_agent')
    || hasTag(conversation, 'support');
  const hasTicketMessage = typeof conversation.lastMessage === 'string' &&
    /assigned to/i.test(conversation.lastMessage);

  // If conversation is explicitly owned by bot, not queued, and no agent is assigned:
  // It is a bot conversation unless it has ticket/live_chat tags or ticket messages.
  if (
    conversation.ownerType === 'bot' &&
    !conversation.assignedTo &&
    conversation.status !== 'queued' &&
    !conversation.metadata?.handoffRequested &&
    !hasLiveAgentTag &&
    !hasTicketMessage
  ) {
    return false;
  }

  // A standard 'open' conversation with no agent assignment and no active handoff is a bot conversation
  if (
    conversation.status === 'open' &&
    !conversation.assignedTo &&
    !conversation.metadata?.handoffRequested &&
    conversation.ownerType !== 'agent' &&
    !hasLiveAgentTag &&
    !hasTicketMessage
  ) {
    return false;
  }

  const isAgentOwner = conversation.ownerType === 'agent';
  const isAssigned = Boolean(conversation.assignedTo);
  const isQueued = conversation.status === 'queued';
  const hasActiveHandoff = Boolean(conversation.metadata?.handoffRequested || conversation.metadata?.handoffQueueActive) && (isAgentOwner || isQueued || isAssigned);

  return Boolean(
    isAssigned ||
    isQueued ||
    hasActiveHandoff ||
    hasTicketMessage ||
    hasLiveAgentTag ||
    (isAgentOwner && ['queued', 'assigned', 'active'].includes(conversation.status)) ||
    (['chat_handoff', 'live_agent', 'live_chat'].includes(getHandoffReason(conversation)) && (isAgentOwner || isQueued || isAssigned)) ||
    (getHandoffReason(conversation) === 'raise_ticket' && ['queued', 'assigned', 'pending'].includes(conversation.status)) ||
    conversation.metadata?.systemEvent === 'conversation.assigned'
  );
};

/** Purpose: listen for new ticket assignments and show global alerts/sound. Why: agents must notice work even when they are viewing another workspace page. */
export default function GlobalTicketNotification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectedBot, selectedEnv, agentPresence } = useAuth();

  const [incomingBanner, setIncomingBanner] = useState(null);
  const [notificationPrefs, setNotificationPrefs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('agent_notification_preferences')) || { browser: false, sound: true };
    } catch {
      return { browser: false, sound: true };
    }
  });

  const recentAlertsRef = useRef(new Map());
  const knownTicketsRef = useRef(new Map());

  // Keep notification preferences in sync with localStorage and custom events
  useEffect(() => {
    // Refresh preferences when another tab or settings panel updates the saved values.
    /** Purpose: Handle handle prefs change in the global ticket notification module. Why: This keeps the corresponding action and its state or request update consistent. */
    const handlePrefsChange = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('agent_notification_preferences'));
        if (stored) setNotificationPrefs(stored);
      } catch {}
    };
    window.addEventListener('agent_notification_preferences_changed', handlePrefsChange);
    window.addEventListener('storage', handlePrefsChange);
    return () => {
      window.removeEventListener('agent_notification_preferences_changed', handlePrefsChange);
      window.removeEventListener('storage', handlePrefsChange);
    };
  }, []);

  // Auto-dismiss floating banner after 9 seconds
  useEffect(() => {
    if (!incomingBanner) return undefined;
    /** Purpose: Dismiss the incoming-ticket banner after its interval. Why: Temporary notifications must not remain on screen indefinitely. */
    const timer = window.setTimeout(() => setIncomingBanner(null), 9000);
    return () => window.clearTimeout(timer);
  }, [incomingBanner]);

  const isAvailable = (agentPresence?.status || 'online') === 'online';

  /** Purpose: Gate and deliver ticket alerts, sound, and desktop notifications. Why: Only relevant available agents should be interrupted. */
  const triggerAlert = useCallback(
    ({ conversation, title, previewText, isTicket = true, isAssignment = false }) => {
      if (!conversation) return;
      const conversationId = normalizeId(conversation._id || conversation.entityId);

      // Check agent availability: ONLY available / online agents receive sound & banners
      const isOnline = (agentPresence?.status || 'online') === 'online';
      if (!isOnline) return;

      // Deduplicate: avoid ringing multiple times within 6 seconds for the same conversation
      const now = Date.now();
      const lastAlertTime = recentAlertsRef.current.get(conversationId) || 0;
      if (now - lastAlertTime < 6000) return;
      recentAlertsRef.current.set(conversationId, now);

      // Clean up stale alert timestamps
      for (const [id, timestamp] of recentAlertsRef.current.entries()) {
        if (now - timestamp > 30000) recentAlertsRef.current.delete(id);
      }

      // 1. Play sound chime
      if (notificationPrefs.sound) {
        playTicketChime();
      }

      // 2. Trigger browser desktop notification
      if (
        (notificationPrefs.browser || isAssignment) &&
        typeof window !== 'undefined' &&
        window.Notification?.permission === 'granted'
      ) {
        try {
          const n = new window.Notification(title, {
            body: previewText,
            icon: '/favicon.ico',
            tag: conversationId,
          });
          /** Purpose: Focus the app and remember the notified conversation before navigation. Why: Desktop notifications must open the correct inbox thread. */
          n.onclick = () => {
            window.focus();
            const isTicketTarget = isTicket || isTicketConversation(conversation);
            const targetSection = isTicketTarget ? 'tickets' : 'chats';
            try {
              sessionStorage.setItem(
                'pending_inbox_conversation',
                JSON.stringify({ conversation, conversationId, section: targetSection }),
              );
            } catch {}
            if (location.pathname.startsWith('/inbox')) {
              window.dispatchEvent(
                new CustomEvent('select_inbox_conversation', { detail: { conversation, conversationId, section: targetSection } }),
              );
              navigate(`/inbox/${targetSection}?conversationId=${conversationId}`, { replace: true });
            } else {
              navigate(`/inbox/${targetSection}?conversationId=${conversationId}`);
            }
          };
        } catch {}
      }

      // 3. Floating in-app alert banner
      setIncomingBanner({
        id: conversationId,
        title,
        badgeText: isAssignment
          ? 'Ticket Assigned to You'
          : (title.includes('Ticket Assigned to') ? title.split(':')[0].replace(/^🎫\s*/, '').trim() : 'New Ticket'),
        displayName: conversation.displayName || conversation.userId || 'Customer',
        previewText,
        conversation,
        channel: conversation.channel || 'web',
        isTicket,
        isAssignment,
      });
    },
    [agentPresence?.status, location.pathname, navigate, notificationPrefs.browser, notificationPrefs.sound],
  );

  useEffect(() => {
    // Adapt the legacy polling event to the same alert flow used by realtime socket events.
    /** Purpose: Handle on polled assignment in the global ticket notification module. Why: This keeps the corresponding action and its state or request update consistent. */
    const onPolledAssignment = (event) => {
      const conversation = event.detail?.conversation;
      if (!conversation) return;
      const displayName = conversation.displayName || conversation.userId || 'Customer';
      triggerAlert({
        conversation,
        title: `🎫 Ticket Assigned to You: ${displayName}`,
        previewText: conversation.lastMessage || 'A ticket was assigned to you.',
        isTicket: true,
        isAssignment: true,
      });
    };

    window.addEventListener('inbox:ticket-assignment-polled', onPolledAssignment);
    return () => window.removeEventListener('inbox:ticket-assignment-polled', onPolledAssignment);
  }, [triggerAlert]);

  useEffect(() => {
    if (!user) return undefined;
    const socket = getSocket();
    if (!socket) return undefined;

    const myAgentId = normalizeId(user._id || user.id || user.userId);
    const roomEnv = selectedEnv || 'dev';

    // Subscribe to bot-wide and agent-specific rooms so both assignment streams are received.
    /** Purpose: Resolve join rooms in the global ticket notification module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
    const joinRooms = () => {
      if (selectedBot) {
        socket.emit('inbox:join', { botId: selectedBot, envName: roomEnv });
      }
      if (myAgentId) {
        socket.emit('agent:join', { agentId: myAgentId, botId: selectedBot, envName: roomEnv });
      }
    };

    socket.on('connect', joinRooms);
    if (socket.connected) {
      joinRooms();
    }

    // 1. Direct assignment / transfer to current agent via notification
    // Recipient filtering prevents another agent's assignment from generating a local alert.
    /** Purpose: Handle on notification in the global ticket notification module. Why: This keeps the corresponding action and its state or request update consistent. */
    const onNotification = (payload = {}) => {
      const notification = payload.notification;
      if (!notification) return;
      const recipientId = normalizeId(notification.recipientId);
      if (recipientId && recipientId !== myAgentId) return;

      const notifType = String(notification.type || '').toLowerCase();
      const isAssignment = ['assignment', 'transfer'].includes(notifType);
      if (!isAssignment) return;

      triggerAlert({
        conversation: {
          _id: notification.entityId,
          displayName: notification.title || 'Assigned Ticket',
          lastMessage: notification.body || 'Open Agent Desk to respond',
          channel: 'web',
        },
        title: notifType === 'transfer' ? 'Conversation Transferred to You' : 'Ticket Assigned to You',
        previewText: notification.body || 'Open Agent Desk to respond',
        isTicket: true,
        isAssignment: true,
      });
    };

    // 2. Direct assign_conversation socket event
    // Handle the direct assignment payload when it targets this bot and available agent.
    /** Purpose: Handle on assign conversation in the global ticket notification module. Why: This keeps the corresponding action and its state or request update consistent. */
    const onAssignConversation = (payload = {}) => {
      const incoming = payload.conversation;
      if (!incoming) return;
      if (selectedBot && incoming.botId && normalizeId(incoming.botId) !== normalizeId(selectedBot)) return;

      const assignedToId = normalizeId(incoming.assignedTo);
      const isAssignedToMe = Boolean(myAgentId && assignedToId && assignedToId === myAgentId);
      const displayName = incoming.displayName || incoming.userId || 'Customer';
      const previewText = incoming.lastMessage || 'Live support requested';

      if (isAssignedToMe || isAvailable) {
        const title = isAssignedToMe
          ? `🎫 Ticket Assigned to You: ${displayName}`
          : `🎫 New Ticket Assigned: ${displayName}`;

        triggerAlert({
          conversation: incoming,
          title,
          previewText,
          isTicket: true,
          isAssignment: isAssignedToMe,
        });
      }
    };

    // 3. Realtime conversation updates
    // Detect only transitions into support work, avoiding alerts for ordinary bot messages.
    /** Purpose: Handle on upsert in the global ticket notification module. Why: This keeps the corresponding action and its state or request update consistent. */
    const onUpsert = (payload = {}) => {
      const incoming = payload.conversation;
      if (!incoming) return;

      // Filter by current bot if selected
      if (selectedBot && normalizeId(incoming.botId) !== normalizeId(selectedBot)) return;

      // STRICT CHECK: Ignore regular bot conversations completely!
      if (!isSupportConversation(incoming)) return;

      const incomingId = normalizeId(incoming._id);
      const prevData = knownTicketsRef.current.get(incomingId);
      const assignedToId = normalizeId(incoming.assignedTo);
      const isAssignedToMe = Boolean(myAgentId && assignedToId && assignedToId === myAgentId);

      // Check if newly transitioned into a support ticket/handoff or assigned
      const isNewTicketArrival =
        !prevData
          ? Boolean(
              incoming.assignedTo ||
              incoming.status === 'queued' ||
              incoming.metadata?.handoffRequested ||
              (incoming.ownerType === 'agent' && getHandoffReason(incoming) === 'chat_handoff')
            )
          : (!prevData.isSupport && true) ||
            (!prevData.handoffRequested && incoming.metadata?.handoffRequested) ||
            (prevData.status !== 'queued' && incoming.status === 'queued') ||
            (!prevData.assignedTo && incoming.assignedTo) ||
            (isAssignedToMe && prevData.assignedTo !== myAgentId);

      knownTicketsRef.current.set(incomingId, {
        isSupport: true,
        handoffRequested: Boolean(incoming.metadata?.handoffRequested),
        status: incoming.status,
        assignedTo: assignedToId,
      });

      // Sound and banner condition:
      // Alert when assigned to me, unassigned ticket, or when any new ticket arrives for an available agent
      if (isNewTicketArrival && (isAssignedToMe || !assignedToId || isAvailable)) {
        const displayName = incoming.displayName || incoming.userId || 'Customer';
        const previewText = incoming.lastMessage || incoming.metadata?.query || 'Live support requested';
        const assignedName = typeof incoming.assignedTo === 'object'
          ? (incoming.assignedTo?.name || incoming.assignedTo?.email || '')
          : '';

        let title;
        if (isAssignedToMe) {
          title = `🎫 Ticket Assigned to You: ${displayName}`;
        } else if (assignedName) {
          title = `🎫 Ticket Assigned to ${assignedName}: ${displayName}`;
        } else {
          title = `🎫 New Support Ticket: ${displayName}`;
        }

        triggerAlert({
          conversation: incoming,
          title,
          previewText,
          isTicket: true,
          isAssignment: isAssignedToMe,
        });
      }
    };

    socket.on('notification.created', onNotification);
    socket.on('assign_conversation', onAssignConversation);
    socket.on('inbox:conversation-upsert', onUpsert);

    return () => {
      socket.off('connect', joinRooms);
      socket.off('notification.created', onNotification);
      socket.off('assign_conversation', onAssignConversation);
      socket.off('inbox:conversation-upsert', onUpsert);
    };
  }, [isAvailable, selectedBot, selectedEnv, triggerAlert, user]);

  // Route the banner action to the proper inbox section and selected conversation.
  /** Purpose: Handle handle open conversation in the global ticket notification module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleOpenConversation = (conversation) => {
    if (!conversation) return;
    const conversationId = normalizeId(conversation._id || conversation.entityId);
    const isBannerTicket = incomingBanner?.isTicket;
    setIncomingBanner(null);

    const isTicket = isBannerTicket ?? isTicketConversation(conversation);
    const targetSection = isTicket ? 'tickets' : 'chats';

    try {
      sessionStorage.setItem(
        'pending_inbox_conversation',
        JSON.stringify({ conversation, conversationId, section: targetSection }),
      );
    } catch {}

    if (location.pathname.startsWith('/inbox')) {
      window.dispatchEvent(
        new CustomEvent('select_inbox_conversation', { detail: { conversation, conversationId, section: targetSection } }),
      );
      navigate(`/inbox/${targetSection}?conversationId=${conversationId}`, { replace: true });
    } else {
      navigate(`/inbox/${targetSection}?conversationId=${conversationId}`);
    }
  };

  return (
    <AnimatePresence>
      {incomingBanner ? (
        <motion.div
          initial={{ opacity: 0, y: -30, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -30, scale: 0.92 }}
          transition={{ type: 'spring', damping: 22, stiffness: 320 }}
          className="fixed top-20 right-6 z-[9999] flex items-center gap-3.5 rounded-2xl border border-blue-500/30 bg-slate-900/95 p-3.5 text-white shadow-2xl backdrop-blur-xl dark:border-blue-400/40 dark:bg-slate-950/95"
          style={{ maxWidth: 420 }}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md">
            <ConfirmationNumberOutlinedIcon sx={{ fontSize: 24 }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-[10px] font-bold tracking-wider text-blue-300 uppercase">
                {incomingBanner.badgeText || (incomingBanner.isAssignment ? 'Ticket Assigned' : 'New Ticket')}
              </span>
              <span className="text-[10px] text-slate-400 font-medium capitalize">
                {incomingBanner.channel}
              </span>
            </div>
            <div className="mt-0.5 truncate text-sm font-bold text-white">
              {incomingBanner.displayName}
            </div>
            <div className="truncate text-xs text-slate-300">
              {incomingBanner.previewText}
            </div>
          </div>
          <div className="flex items-center gap-1.5 pl-1">
            <button
              type="button"
              onClick={() => handleOpenConversation(incomingBanner.conversation)}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-blue-500 transition-colors cursor-pointer"
            >
              Open
            </button>
            <button
              type="button"
              onClick={() => setIncomingBanner(null)}
              className="rounded-lg p-1.5 text-slate-400 hover:text-white transition-colors cursor-pointer"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
